import axios from 'axios';
import { API_ENDPOINT, CLIENT_ID } from 'constants/configs';
import { getClientConfig, getSiteKey } from 'constants/clients';
import { getCookie, saveUserCookies } from 'helpers/cookies';

// ----------------------------
// Resolver config actual
// ----------------------------
const resolveClient = () => getClientConfig() || null;
const resolveClientId = () => resolveClient()?.clientId ?? CLIENT_ID;
const resolveClientToken = () => resolveClient()?.clientToken ?? null;
const resolveOrigin = () => (typeof window !== 'undefined' ? window.location.origin : '');
const isValidClientId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
};

// El front prioriza client_token cifrado. client_id queda solo como compatibilidad
// para tiendas viejas o entornos donde todavia no llego el bootstrap nuevo.
const resolveClientParams = () => {
  const origin = resolveOrigin();
  const originParam = origin ? { origin } : {};
  const clientToken = resolveClientToken();
  if (clientToken) return { ...originParam, client_token: clientToken };

  const clientId = resolveClientId();
  return isValidClientId(clientId) ? { ...originParam, client_id: Number(clientId) } : originParam;
};
export const getResolvedClientName = () => resolveClient()?.name ?? getSiteKey() ?? null;

// ----------------------------
// Axios base
// ----------------------------
const api = axios.create({
  baseURL: API_ENDPOINT,
});

// Refresh token 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error?.config || {};
    if (error?.response?.status === 401 && !originalRequest.retry) {
      originalRequest.retry = true;
      return api
        .post('auth/refreshToken', { refreshToken: getCookie('refreshToken') })
        .then(({ data }) => {
          saveUserCookies(data.token);
          const accessToken = getCookie('accessToken');
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axios(originalRequest);
        });
    }
    return Promise.reject(error);
  },
);

// A server outage or an unreachable API should give the storefront one clear state.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const isNetworkFailure = !error?.response && !axios.isCancel(error);
    const requestPath = String(error?.config?.url || '').split('?')[0].replace(/^\/+/, '');
    const isAbandonedCartSnapshot = requestPath.endsWith('auth/cart-snapshot');

    if (status >= 500 || isNetworkFailure) {
      if (isAbandonedCartSnapshot) {
        // The outbox retries this endpoint a bounded number of times and logs
        // once after the final failure.
      } else {
        console.error('Storefront API unavailable:', error);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('app:maintenance'));
        }
      }
    }

    return Promise.reject(error);
  },
);

// ----------------------------
// Helpers comunes
// ----------------------------
const getOptions = () => {
  const accessToken = getCookie('accessToken');

  // No mandamos headers custom de tienda en lecturas públicas:
  // el token viaja como client_token en body/query y el navegador ya manda Origin.
  // Así evitamos preflights CORS innecesarios en producción.
  return {
    'Content-Type': 'application/json',
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      Accept: 'application/json',
    },
  };
};

const errorResponse = (error) => {
  const message = error?.response?.data?.message || error?.message || 'Request error';
  const response = new Error(message);
  response.errors = error?.response?.data?.errors || null;
  return response;
};

// ----------------------------
// Métodos públicos
// ----------------------------
export const post = async (url, params = {}, headers = {}) => {
  try {
    const options = { ...getOptions(), ...headers };
    const clientParams = resolveClientParams();
    const isFormData = typeof FormData !== 'undefined' && params instanceof FormData;
    const sendParams = isFormData ? params : { ...params, ...clientParams };

    if (isFormData) {
      // En formularios con imagen/archivo no recreamos el body: agregamos la tienda
      // al FormData existente para no perder uploads ni boundary del navegador.
      Object.entries(clientParams).forEach(([key, value]) => {
        if (!sendParams.has(key)) {
          sendParams.append(key, value);
        }
      });
    }

    const { data } = await api.post(url, sendParams, options);
    return data;
  } catch (error) {
    throw errorResponse(error);
  }
};

export const get = async (url, headers = {}) => {
  try {
    const baseOpts = getOptions();
    const mergedParams = { ...(headers?.params || {}), ...resolveClientParams() };

    const options = {
      ...baseOpts,
      ...headers,
      params: mergedParams,
    };

    const { data } = await api.get(url, options);
    return data;
  } catch (error) {
    throw errorResponse(error);
  }
};
